import { useParams, useNavigate } from "react-router-dom";
import { usePurchaseOrderDetails } from "../../hooks/usePurchaseOrderDetails";
import { useMarkPurchaseOrderDelivered } from "../../hooks/useMarkPurchaseOrderDelivered";
import { useImageUpload } from "../../../../hooks/useImageUpload";
import { PageContentSkeleton } from "../../../../components/common/page-content-skeleton";
import { PageContainer } from "../../../../components/common/page-container";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { AppCard } from "../../../../components/common/card";
import { Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { ImageUpload } from "../../../../components/common/image-upload";
import { PurchaseOrderNotFound } from "../components/purchase-order-not-found";

export function ConfirmDeliveryPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const purchaseOrderId = Number(id ?? 0);

  const {
    data: purchaseOrder,
    error,
    isLoading,
  } = usePurchaseOrderDetails(purchaseOrderId);

  const { markDelivered, isLoading: isSubmitting } =
    useMarkPurchaseOrderDelivered();

  const imageUpload = useImageUpload({
    returnAsFile: true, // We need the File object for multipart upload
    maxSizeMB: 5,
  });

  const handleSubmit = async () => {
    if (!imageUpload.selectedFile) {
      return;
    }

    try {
      await markDelivered(purchaseOrderId, imageUpload.selectedFile);
    } catch {
      // Error is handled in the hook
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageContentSkeleton cardCount={2} actionCount={1} />
      </PageContainer>
    );
  }

  if (error || !purchaseOrder) {
    return (
      <PageContainer>
        <PurchaseOrderNotFound />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <AppCard title="Confirm Delivery" color="bg-bgDefault" className="w-full max-sm:px-4">
        <div className="flex flex-col gap-6">
          <div>
            <Text variant="body" tone="body" className="mb-2">
              Purchase Order: {purchaseOrder.name}
            </Text>
            <Text variant="bodyLg" tone="dark">
              Please upload a photo (and optionally a delivery tracking link) to
              confirm delivery of this purchase order.
            </Text>
          </div>

          <ImageUpload
            title="Delivery Confirmation Photo"
            hint="Upload a photo showing the delivered items (PNG or JPG, max 5MB)"
            uploadLabel="Click to upload delivery photo"
            maxSizeMB={5}
            disabled={isSubmitting}
            imageUpload={imageUpload}
            previewSize="medium"
          />

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4 border-t border-uiSecondary">
            <Button variant="secondary"
              onClick={() =>
                navigate(
                  `${AppRoutes.PURCHASE_ORDER_DETAILS.replace(
                    ":id",
                    purchaseOrderId.toString()
                  )}`
                )
              }
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button variant="primary"
              onClick={handleSubmit}
              disabled={!imageUpload.selectedFile || isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? "Submitting..." : "Confirm Delivery"}
            </Button>
          </div>
        </div>
      </AppCard>
    </PageContainer>
  );
}
